import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useJobStore } from '../../stores/jobStore';
import { Button } from '../common/Button';
import ScrollableContainer from '../common/ScrollableContainer';
import GridTable from '../common/GridTable';
import i18n from '../../i18n';
import EyeButton from '../common/EyeButton';
import JobModal from './JobModal';
import JobPublishedDate from '../jobs/JobPublishedDate';
import JobListingType from '../jobs/JobListingType';
import JobTags from '../jobs/JobTags';
import { LoadingSpinner } from '../LoadingSpinner';

const JOBS_COUNT = 3;

const JobsSection: React.FC = () => {
  const { t } = useTranslation(['jobs', 'common', 'dashboard']);
  const { loading, jobs, fetchJobs } = useJobStore();

  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const selectedJob = selectedJobId ? (jobs.find(job => job.id === selectedJobId) ?? null) : null;

  const closeModal = () => setSelectedJobId(null);
  const visibleJobsLimited = jobs.slice(0, JOBS_COUNT);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  return (
    <section className="relative rounded-[25px] bg-white p-6 shadow-[0px_3px_12px_0px_rgba(13,45,57,0.14)] md:p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-[25px] font-semibold leading-[30px] text-secondary">
          {t('dashboard:jobs.title')}
        </h1>

        <Button
          variant="transparent-with-border"
          href="/jobs"
          className="hidden h-10 w-[321px] rounded-full md:inline-flex"
        >
          {t('dashboard:jobs.view_all')}
        </Button>
      </div>

      {loading && visibleJobsLimited.length === 0 && (
        <div className="flex h-10 items-center justify-center">
          <LoadingSpinner />
        </div>
      )}

      {visibleJobsLimited.length === 0 && !loading && (
        <div className="mb-4 flex h-10 items-center justify-center text-sm text-gray-500">
          {t('jobs:noJobs')}
        </div>
      )}

      {jobs.length > 0 && (
        <ScrollableContainer>
          <GridTable className="grid-cols-[1fr_auto_auto_auto_auto] divide-y-[2px] divide-[#E6EDF4]">
            {visibleJobsLimited.map((job, index) => {
              const isFeatured = job.listing_type?.code === 'featured';
              const isFirst = index === 0;

              return (
                <React.Fragment key={job.id}>
                  {/* Job Title */}
                  <GridTable.Cell
                    className={`pl-0 cursor-pointer min-w-0 ${isFirst ? '!border-t-0' : ''}`}
                    role="button"
                    onClick={() => setSelectedJobId(job.id)}
                  >
                    <p className="truncate text-[15px] font-semibold leading-[20px] text-secondary">
                      {job.title}
                    </p>
                  </GridTable.Cell>

                  {/* Status Badge (JobListingType) */}
                  <GridTable.Cell
                    className={`cursor-pointer whitespace-nowrap justify-end ${isFirst ? '!border-t-0' : ''}`}
                    role="button"
                    onClick={() => setSelectedJobId(job.id)}
                  >
                    <JobListingType job={job} t={t} className={isFeatured ? 'bg-[#FF564A]' : ''} />
                  </GridTable.Cell>

                  {/* Publication Date */}
                  <GridTable.Cell
                    className={`cursor-pointer whitespace-nowrap justify-end ${isFirst ? '!border-t-0' : ''}`}
                    role="button"
                    onClick={() => setSelectedJobId(job.id)}
                  >
                    <JobPublishedDate
                      job={job}
                      t={t}
                      language={i18n.language}
                      className="text-right [&>span:first-child]:text-[15px] [&>span:first-child]:leading-[20px] [&>span:last-child]:text-[13px] [&>span:last-child]:leading-[20px] [&>span:last-child]:font-semibold"
                    />
                  </GridTable.Cell>

                  {/* Job Tags */}
                  <GridTable.Cell
                    className={`cursor-pointer min-w-0 justify-end ${isFirst ? '!border-t-0' : ''}`}
                    role="button"
                    onClick={() => setSelectedJobId(job.id)}
                  >
                    <JobTags
                      job={job}
                      t={t}
                      projectMode={false}
                      className="justify-end [&_span]:!whitespace-nowrap [&_span]:!break-normal"
                    />
                  </GridTable.Cell>

                  {/* Eye Button */}
                  <GridTable.Cell
                    className={`pr-0 justify-end cursor-pointer flex-shrink-0 ${isFirst ? '!border-t-0' : ''}`}
                    role="button"
                    onClick={() => setSelectedJobId(job.id)}
                  >
                    <EyeButton
                      ariaLabel={t('viewJob', { ns: 'jobs' })}
                      onClick={() => setSelectedJobId(job.id)}
                    />
                  </GridTable.Cell>
                </React.Fragment>
              );
            })}
          </GridTable>
        </ScrollableContainer>
      )}

      {selectedJob && <JobModal job={selectedJob} onClose={closeModal} />}

      <div className="mt-4 sm:hidden">
        <Link
          to="/jobs"
          className="block rounded-full border border-primary px-6 py-2 text-center font-bold text-secondary"
        >
          {t('dashboard:jobs.view_all')}
        </Link>
      </div>
    </section>
  );
};

export default JobsSection;
